import InfoCircleFilled from '@ant-design/icons/lib/icons/InfoCircleFilled'
import CloseCircleFilled from '@ant-design/icons/lib/icons/CloseCircleFilled'
import { Popover } from 'antd/lib'
import { isEmpty, map } from 'lodash'
import { ViolationType, ViolationsType } from 'shared/violations/types'
import classes from './Violations.module.less'
import { sendAnalyticsTrack } from 'Utils/analytics.utils.ts'
import { useCallback } from 'react'
import { ContainerType, TaskConfigType } from 'Types/Tasks.types'

export type ViolationsProps = {
    violations?: ViolationsType
    taskConfig: TaskConfigType<ContainerType, unknown>
}

type ViolationItemPropsType = {
    violation: ViolationType
    type: 'error' | 'warning'
}

const ViolationItem: React.FC<ViolationItemPropsType> = (props: ViolationItemPropsType) => {
    return (
        <div className={classes.violationItem}>
            <div className={classes.iconContainer}>
                {props.type === 'warning' ? (
                    <InfoCircleFilled className={classes.warningIcon} />
                ) : (
                    <CloseCircleFilled className={classes.errorIcon} />
                )}
            </div>
            <div className={classes.violationLabel}>{props.violation.message}</div>
        </div>
    )
}

const WarningContainer: React.FC<{ violations?: ViolationsType }> = (props: {
    violations?: ViolationsType
}) => {
    return (
        <>
            {!isEmpty(props.violations?.warning) ? (
                map(props.violations?.warning, violation => (
                    <ViolationItem violation={violation} key={violation.id} type="warning" />
                ))
            ) : (
                <div>No warnings found</div>
            )}
        </>
    )
}

const ErrorContainer: React.FC<{ violations?: ViolationsType }> = (props: {
    violations?: ViolationsType
}) => {
    return (
        <>
            {!isEmpty(props.violations?.error) ? (
                map(props.violations?.error, violation => (
                    <ViolationItem violation={violation} key={violation.id} type="error" />
                ))
            ) : (
                <div>No errors found</div>
            )}
        </>
    )
}

export const Violations: React.FC<ViolationsProps> = (props: ViolationsProps) => {
    const getWarningCount = useCallback(() => {
        return props.violations?.warning ? Object.keys(props.violations.warning).length : 0
    }, [props.violations?.warning])

    const getErrorCount = useCallback(() => {
        return props.violations?.error ? Object.keys(props.violations.error).length : 0
    }, [props.violations?.error])

    const getCommonAnalyticsObject = useCallback(() => {
        return {
            project_id: props.taskConfig.assignment.project_id,
            task_id: props.taskConfig.assignment.task.id,
            user_id: props.taskConfig.assignment.user_id,
            step_id: props.taskConfig.assignment.step?.id,
        }
    }, [
        props.taskConfig.assignment.project_id,
        props.taskConfig.assignment.task.id,
        props.taskConfig.assignment.user_id,
        props.taskConfig.assignment.step?.id,
    ])

    return (
        <div className={classes.container} data-testid="violations-wrapper">
            <button
                className={classes.error}
                onClick={() => {
                    sendAnalyticsTrack('PnwWarnings_Clicked', {
                        ...getCommonAnalyticsObject(),
                        count_of_warning: getWarningCount(),
                    })
                }}
            >
                <div className={classes.iconContainer}>
                    <InfoCircleFilled className={classes.warningIcon} />
                </div>
                <div className={classes.label}>
                    {isEmpty(props.violations?.warning)
                        ? 0
                        : Object.keys(props.violations?.warning ?? {}).length}
                </div>
                <Popover
                    content={<WarningContainer violations={props.violations} />}
                    trigger={'click'}
                >
                    <div className={classes.caretDown} />
                </Popover>
            </button>
            <button
                className={classes.warn}
                onClick={() => {
                    sendAnalyticsTrack('PnwErrors_Clicked', {
                        ...getCommonAnalyticsObject(),
                        count_of_error: getErrorCount(),
                    })
                }}
            >
                <div className={classes.iconContainer}>
                    <CloseCircleFilled className={classes.errorIcon} />
                </div>
                <div className={classes.label}>
                    {isEmpty(props.violations?.error)
                        ? 0
                        : Object.keys(props.violations?.error ?? {}).length}
                </div>
                <Popover
                    content={<ErrorContainer violations={props.violations} />}
                    trigger={'click'}
                >
                    <div className={classes.caretDown} />
                </Popover>
            </button>
        </div>
    )
}
